import { create } from "zustand";
import { nanoid } from "nanoid";
import i18n from "../i18n";
import { buildMiniEsgAuthHeaders, getMiniEsgApiBase, MINI_ESG_TOKEN_KEY } from "../utils/miniEsgApi";

/**
 * EVAT Mini ESG™ Store (v1)
 * 
 * Simplified 15-question ESG assessment for SMEs (Small and Medium Enterprises)
 * 3 blocks: Environment (6), Social (5), Governance (4)
 * Scoring: 0-2 points per question (0-30 total)
 */

export type EnergySource = "100% grid" | "partially renewable" | "fully renewable";
export type ESGBlock = "environment" | "social" | "governance";

export interface MiniESGAnswers {
  // Environment (6 questions)
  env1_energyKWh?: number; // Annual electricity consumption
  env2_energySource?: EnergySource; // Energy sources
  env3_wasteManagement?: boolean; // Waste management/separation
  env4_ecoMaterials?: boolean; // Eco-friendly materials/packaging
  env5_co2Data?: boolean; // CO2 emission data available
  env6_shortTermGoals?: boolean; // Short-term environmental goals
  
  // Social (5 questions)
  social1_employeeCount?: number; // Number of employees
  social2_workplaceSafety?: boolean; // Workplace safety system
  social3_trainingProgram?: boolean; // Internal training program
  social4_equalityPolicy?: boolean; // Equality/discrimination policy
  social5_socialProjects?: boolean; // Social projects (sponsorship, donations)
  
  // Governance (4 questions)
  gov1_compliancePolicy?: boolean; // Compliance policy
  gov2_ethicsCode?: boolean; // Ethics code
  gov3_dataSecurity?: boolean; // Data security system (GDPR basics)
  gov4_sustainabilityReporting?: boolean; // Annual sustainability reporting
}

export interface MiniESGScore {
  environment: number; // 0-12 points
  social: number; // 0-10 points
  governance: number; // 0-8 points
  total: number; // 0-30 points
  level: "red" | "yellow" | "green"; // 0-10: red, 11-20: yellow, 21-30: green
}

export interface MiniESGState {
  companyName: string;
  taxId: string;
  sector?: string;
  region?: string;
  employeeBand?: string;
  benchmarkGroup?: string;
  answers: MiniESGAnswers;
  score: MiniESGScore;
  recommendations: string[];
  reportDate: string;
  savedReportId?: string;
  qrCodeUrl?: string;
  energyUnit?: 'kWh' | 'MWh';
  visibility?: 'public' | 'private';
  reportPassword?: string;
  
  // Actions
  setCompanyInfo: (name: string, taxId: string) => void;
  setCompanyMetadata: (meta: { sector?: string; region?: string; employeeBand?: string; benchmarkGroup?: string }) => void;
  setAnswer: (key: keyof MiniESGAnswers, value: any) => void;
  setEnergyUnit: (unit: 'kWh' | 'MWh') => void;
  setVisibility: (visibility: 'public' | 'private') => void;
  calculateScore: () => void;
  generateRecommendations: () => void;
  reset: () => void;
  
  // Backend actions
  saveReport: () => Promise<{ id: string; qrCodeUrl: string } | null>;
  updateReport: (id: string) => Promise<boolean>;
  loadReport: (id: string, password?: string) => Promise<boolean>;
  deleteReport: (id: string) => Promise<boolean>;
  generateQRCodeUrl: () => Promise<string | null>;
  listReports: () => Promise<Array<{
    id: string;
    companyName: string;
    taxId: string;
    score: MiniESGScore;
    sector?: string;
    region?: string;
    employeeBand?: string;
    createdAt: number;
    qrCodeUrl: string;
  }>>;
  
  // Import/Export actions
  importReport: (data: {
    companyName?: string;
    taxId?: string;
    answers?: MiniESGAnswers;
    score?: MiniESGScore;
    recommendations?: string[];
    reportDate?: string;
  }) => void;

  // Auth helpers
  login: (email: string, password: string) => Promise<boolean>;
  requestCopilot: (payload: { mode: 'form' | 'summary' | 'analyst'; prompt?: string; stream?: boolean; onChunk?: (chunk: string) => void }) => Promise<any>;
}

const calculateQuestionScore = (value: any): number => {
  if (value === undefined || value === null || value === false || value === 0 || value === "") {
    return 0; // No data / no system / no goal
  }
  
  if (typeof value === "boolean") {
    return value ? 1 : 0; // Basic solution = 1 point
  }
  
  if (typeof value === "number") {
    if (value > 0) return 2; // Measurable data = 2 points
    return 0;
  }
  
  if (typeof value === "string") {
    if (value === "100% grid") return 1; // Basic
    if (value === "partially renewable") return 1; // Basic
    if (value === "fully renewable") return 2; // Documented, measurable
    return value.length > 0 ? 1 : 0;
  }
  
  return 0;
};

const calculateBlockScore = (answers: MiniESGAnswers, block: ESGBlock): number => {
  let score = 0;
  
  if (block === "environment") {
    score += calculateQuestionScore(answers.env1_energyKWh);
    score += calculateQuestionScore(answers.env2_energySource);
    score += calculateQuestionScore(answers.env3_wasteManagement);
    score += calculateQuestionScore(answers.env4_ecoMaterials);
    score += calculateQuestionScore(answers.env5_co2Data);
    score += calculateQuestionScore(answers.env6_shortTermGoals);
  } else if (block === "social") {
    score += calculateQuestionScore(answers.social1_employeeCount);
    score += calculateQuestionScore(answers.social2_workplaceSafety);
    score += calculateQuestionScore(answers.social3_trainingProgram);
    score += calculateQuestionScore(answers.social4_equalityPolicy);
    score += calculateQuestionScore(answers.social5_socialProjects);
  } else if (block === "governance") {
    score += calculateQuestionScore(answers.gov1_compliancePolicy);
    score += calculateQuestionScore(answers.gov2_ethicsCode);
    score += calculateQuestionScore(answers.gov3_dataSecurity);
    score += calculateQuestionScore(answers.gov4_sustainabilityReporting);
  }
  
  return score;
};

const generateRecommendations = (score: MiniESGScore, answers: MiniESGAnswers, locale?: string): string[] => {
  // Default to current i18n language or 'en'
  const currentLocale = locale || i18n.language || 'en';
  const recommendations: string[] = [];
  
  // English recommendations
  const recommendationsEn = {
    env1_missing: "Measure and document annual electricity consumption (kWh).",
    env2_renewable: "Consider introducing renewable energy sources (solar panels, green energy contracts).",
    env3_waste: "Establish a selective waste collection system.",
    social2_safety: "Create a workplace safety system and document it.",
    social3_training: "Start an internal training program for employee development.",
    gov1_compliance: "Formulate a compliance policy.",
    gov3_gdpr: "Establish a GDPR-compatible data security system.",
    general1: "Continue current ESG practices.",
    general2: "Document sustainability measures annually.",
    general3: "Consider the possibility of an external ESG audit."
  };
  
  // Hungarian recommendations
  const recommendationsHu = {
    env1_missing: "Mérje fel az éves villamosenergia-fogyasztást (kWh) és dokumentálja.",
    env2_renewable: "Fontolja meg a megújuló energiaforrások bevezetését (napelem, zöldenergia-szerződés).",
    env3_waste: "Alapítson szelektív hulladékgyűjtési rendszert.",
    social2_safety: "Hozzon létre munkavédelmi rendszert és dokumentálja.",
    social3_training: "Indítson belső képzési programot a munkavállalók fejlesztésére.",
    gov1_compliance: "Fogalmazzon meg megfelelőségi szabályzatot.",
    gov3_gdpr: "Alapítson GDPR-kompatibilis adatbiztonsági rendszert.",
    general1: "Folytassa a jelenlegi ESG gyakorlatokat.",
    general2: "Dokumentálja a fenntarthatósági intézkedéseket évente.",
    general3: "Fontolja meg a külső ESG audit lehetőségét."
  };
  
  const rec = currentLocale === 'hu' ? recommendationsHu : recommendationsEn;
  
  // Environment recommendations
  if (score.environment < 6) {
    if (!answers.env1_energyKWh) {
      recommendations.push(rec.env1_missing);
    }
    if (!answers.env2_energySource || answers.env2_energySource === "100% grid") {
      recommendations.push(rec.env2_renewable);
    }
    if (!answers.env3_wasteManagement) {
      recommendations.push(rec.env3_waste);
    }
  }
  
  // Social recommendations
  if (score.social < 5) {
    if (!answers.social2_workplaceSafety) {
      recommendations.push(rec.social2_safety);
    }
    if (!answers.social3_trainingProgram) {
      recommendations.push(rec.social3_training);
    }
  }
  
  // Governance recommendations
  if (score.governance < 4) {
    if (!answers.gov1_compliancePolicy) {
      recommendations.push(rec.gov1_compliance);
    }
    if (!answers.gov3_dataSecurity) {
      recommendations.push(rec.gov3_gdpr);
    }
  }
  
  // If no specific recommendations, add general ones
  if (recommendations.length === 0) {
    recommendations.push(rec.general1);
    recommendations.push(rec.general2);
    recommendations.push(rec.general3);
  }
  
  return recommendations.slice(0, 3); // Max 3 recommendations
};

const STORAGE_KEY = 'miniESG_autosave';
const TOKEN_KEY = MINI_ESG_TOKEN_KEY;

const loadAutoSave = () => {
  if (typeof window === 'undefined') return null;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Failed to load auto-save:', e);
  }
  return null;
};

const saveAutoSave = (state: Partial<MiniESGState>) => {
  if (typeof window === 'undefined') return;
  try {
    const data = {
      companyName: state.companyName || '',
      taxId: state.taxId || '',
      sector: state.sector || '',
      region: state.region || '',
      employeeBand: state.employeeBand || '',
      benchmarkGroup: state.benchmarkGroup || '',
      answers: state.answers || {},
      reportDate: state.reportDate || new Date().toISOString().split('T')[0],
      energyUnit: state.energyUnit || 'kWh',
      visibility: state.visibility || 'public',
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.error('Failed to save auto-save:', e);
  }
};

const clearAutoSave = () => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error('Failed to clear auto-save:', e);
  }
};

const API_BASE = getMiniEsgApiBase();

export const useMiniESGStore = create<MiniESGState>((set, get) => {
  const calculateScore = (): MiniESGScore => {
    const { answers } = get();
    
    const environment = calculateBlockScore(answers, "environment");
    const social = calculateBlockScore(answers, "social");
    const governance = calculateBlockScore(answers, "governance");
    const total = environment + social + governance;
    
    let level: "red" | "yellow" | "green";
    if (total <= 10) {
      level = "red";
    } else if (total <= 20) {
      level = "yellow";
    } else {
      level = "green";
    }
    
    return { environment, social, governance, total, level };
  };

  // Load auto-save on initialization
  const autoSaveData = loadAutoSave();
  const initialAnswers = autoSaveData?.answers || {};
  const initialScore = Object.keys(initialAnswers).length > 0 
    ? (() => {
        const env = calculateBlockScore(initialAnswers, "environment");
        const soc = calculateBlockScore(initialAnswers, "social");
        const gov = calculateBlockScore(initialAnswers, "governance");
        const total = env + soc + gov;
        const level = total <= 10 ? "red" : total <= 20 ? "yellow" : "green";
        return { environment: env, social: soc, governance: gov, total, level };
      })()
    : { environment: 0, social: 0, governance: 0, total: 0, level: "red" };
  
  const authHeaders = () => buildMiniEsgAuthHeaders();

  return {
    companyName: autoSaveData?.companyName || "",
    taxId: autoSaveData?.taxId || "",
    sector: autoSaveData?.sector || "",
    region: autoSaveData?.region || "",
    employeeBand: autoSaveData?.employeeBand || "",
    benchmarkGroup: autoSaveData?.benchmarkGroup || "",
    answers: initialAnswers,
    score: initialScore,
    recommendations: Object.keys(initialAnswers).length > 0 ? generateRecommendations(initialScore, initialAnswers) : [],
    reportDate: autoSaveData?.reportDate || new Date().toISOString().split('T')[0],
    energyUnit: autoSaveData?.energyUnit || 'kWh',
    visibility: autoSaveData?.visibility || 'public',
    
    setCompanyInfo: (name, taxId) => {
      set((state) => {
        const newState = { companyName: name, taxId };
        saveAutoSave({ ...state, ...newState });
        return newState;
      });
    },

    setCompanyMetadata: (meta) => {
      set((state) => {
        const newState = { ...meta } as Partial<MiniESGState>;
        saveAutoSave({ ...state, ...newState });
        return newState as any;
      });
    },
    
    setAnswer: (key, value) => {
      set((state) => {
        const newAnswers = { ...state.answers, [key]: value };
        const newScore = calculateScore();
        const newRecommendations = generateRecommendations(newScore, newAnswers);
        const newState = {
          answers: newAnswers,
          score: newScore,
          recommendations: newRecommendations,
        };
        saveAutoSave({ ...state, ...newState });
        return newState;
      });
    },
    
    setEnergyUnit: (unit) => {
      set((state) => {
        const newState = { energyUnit: unit } as Partial<MiniESGState>;
        saveAutoSave({ ...state, ...newState });
        return newState as any;
      });
    },
    
    setVisibility: (visibility: 'public' | 'private') => {
      set((state) => {
        const newState = { visibility } as Partial<MiniESGState>;
        saveAutoSave({ ...state, ...newState });
        return newState as any;
      });
    },
    
    calculateScore: () => {
      const score = calculateScore();
      const { answers } = get();
      const recommendations = generateRecommendations(score, answers);
      
      set({ score, recommendations });
    },
    
    generateRecommendations: () => {
      const { score, answers } = get();
      const recommendations = generateRecommendations(score, answers);
      set({ recommendations });
    },
    
    reset: () => {
      clearAutoSave();
      set({
        companyName: "",
        taxId: "",
        sector: "",
        region: "",
        employeeBand: "",
        benchmarkGroup: "",
        answers: {},
        score: { environment: 0, social: 0, governance: 0, total: 0, level: "red" },
        recommendations: [],
        reportDate: new Date().toISOString().split('T')[0],
        savedReportId: undefined,
        qrCodeUrl: undefined,
      });
    },
    
    // Backend actions
    saveReport: async () => {
      const state = get();
      const reportData = {
        companyName: state.companyName,
        taxId: state.taxId,
        sector: state.sector || undefined,
        region: state.region || undefined,
        employeeBand: state.employeeBand || undefined,
        benchmarkGroup: state.benchmarkGroup || undefined,
        answers: state.answers,
        score: state.score,
        recommendations: state.recommendations,
        reportDate: state.reportDate,
        visibility: state.visibility || 'public',
        password: state.reportPassword || undefined,
      };
      
      try {
        const response = await fetch(`${API_BASE}/report/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(reportData),
        });
        
        if (!response.ok) {
          throw new Error(`Failed to save report: ${response.statusText}`);
        }
        
        const result = await response.json();
        set({ savedReportId: result.id, qrCodeUrl: result.qrCodeUrl });
        clearAutoSave(); // Clear auto-save after successful server save
        return result;
      } catch (error) {
        console.error('Failed to save report:', error);
        return null;
      }
    },
    
    loadReport: async (id: string, password?: string) => {
      try {
        const response = await fetch(`${API_BASE}/report/${id}`, {
          headers: password ? { 'x-report-password': password } : undefined,
        });
        
        if (!response.ok) {
          throw new Error(`Failed to load report: ${response.statusText}`);
        }
        
        const report = await response.json();
        set({
          companyName: report.companyName,
          taxId: report.taxId,
          sector: report.sector || '',
          region: report.region || '',
          employeeBand: report.employeeBand || '',
          benchmarkGroup: report.benchmarkGroup || '',
          answers: report.answers,
          score: report.score,
          recommendations: report.recommendations,
          reportDate: report.reportDate,
          savedReportId: report.id,
          qrCodeUrl: report.qrCodeUrl,
        });
        
        return true;
      } catch (error) {
        console.error('Failed to load report:', error);
        return false;
      }
    },
    
    generateQRCodeUrl: async () => {
      const { savedReportId } = get();
      
      if (!savedReportId) {
        // Save report first if not saved
        const saved = await get().saveReport();
        if (!saved) return null;
        return saved.qrCodeUrl;
      }
      
      try {
        const response = await fetch(`${API_BASE}/report/${savedReportId}/qrcode`, {
          method: 'POST',
        });
        
        if (!response.ok) {
          throw new Error(`Failed to generate QR code URL: ${response.statusText}`);
        }
        
        const result = await response.json();
        set({ qrCodeUrl: result.qrCodeUrl });
        return result.qrCodeUrl;
      } catch (error) {
        console.error('Failed to generate QR code URL:', error);
        return null;
      }
    },
    
    importReport: (data) => {
      set((state) => {
        const newAnswers = data.answers ?? state.answers;
        // Calculate score from imported answers if score not provided
        const newScore = data.score ?? (() => {
          const env = calculateBlockScore(newAnswers, "environment");
          const soc = calculateBlockScore(newAnswers, "social");
          const gov = calculateBlockScore(newAnswers, "governance");
          const total = env + soc + gov;
          const level = total <= 10 ? "red" : total <= 20 ? "yellow" : "green";
          return { environment: env, social: soc, governance: gov, total, level };
        })();
        const newRecommendations = data.recommendations ?? generateRecommendations(newScore, newAnswers);
        
        return {
          companyName: data.companyName ?? state.companyName,
          taxId: data.taxId ?? state.taxId,
          sector: data.sector ?? state.sector,
          region: data.region ?? state.region,
          employeeBand: data.employeeBand ?? state.employeeBand,
          benchmarkGroup: data.benchmarkGroup ?? state.benchmarkGroup,
          answers: newAnswers,
          score: newScore,
          recommendations: newRecommendations,
          reportDate: data.reportDate ?? state.reportDate,
        };
      });
    },
    
    listReports: async () => {
      try {
        const response = await fetch(`${API_BASE}/reports`, {
          headers: authHeaders(),
        });
        
        if (!response.ok) {
          throw new Error(`Failed to list reports: ${response.statusText}`);
        }
        
        const result = await response.json();
        return result.reports || [];
      } catch (error) {
        console.error('Failed to list reports:', error);
        return [];
      }
    },
    
    updateReport: async (id: string) => {
      const state = get();
      const reportData = {
        companyName: state.companyName,
        taxId: state.taxId,
        sector: state.sector || undefined,
        region: state.region || undefined,
        employeeBand: state.employeeBand || undefined,
        benchmarkGroup: state.benchmarkGroup || undefined,
        answers: state.answers,
        score: state.score,
        recommendations: state.recommendations,
        reportDate: state.reportDate,
        visibility: state.visibility || 'public',
        password: state.reportPassword || undefined,
      };
      
      try {
        const response = await fetch(`${API_BASE}/report/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(reportData),
        });
        
        if (!response.ok) {
          throw new Error(`Failed to update report: ${response.statusText}`);
        }
        
        return true;
      } catch (error) {
        console.error('Failed to update report:', error);
        return false;
      }
    },
    
    deleteReport: async (id: string) => {
      try {
        const response = await fetch(`${API_BASE}/report/${id}`, {
          method: 'DELETE',
          headers: authHeaders(),
        });
        
        if (!response.ok) {
          throw new Error(`Failed to delete report: ${response.statusText}`);
        }
        
        // Clear saved report ID if it was the deleted one
        const state = get();
        if (state.savedReportId === id) {
          set({ savedReportId: undefined, qrCodeUrl: undefined });
        }
        
        return true;
      } catch (error) {
        console.error('Failed to delete report:', error);
        return false;
      }
    },

    // Auth helpers
    login: async (email: string, password: string) => {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      if (!res.ok) throw new Error('Login failed');
      const json = await res.json();
      if (json?.token) {
        localStorage.setItem(TOKEN_KEY, json.token);
        return true;
      }
      return false;
    },

    requestCopilot: async ({ mode, prompt, stream, onChunk }) => {
      const state = get();
      try {
        const response = await fetch(`${API_BASE}/ai/copilot`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({
            mode,
            prompt,
            language: i18n.language || 'en',
            companyName: state.companyName,
            score: state.score,
            answers: state.answers,
            stream: stream || false,
          }),
        });
        if (!response.ok) {
          throw new Error(`Copilot error: ${response.statusText}`);
        }
        const contentType = response.headers.get('content-type') || '';
        if (stream && contentType.includes('text/event-stream') && response.body) {
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let text = '';
          while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            if (value) {
              const chunk = decoder.decode(value, { stream: true });
              text += chunk;
              onChunk?.(chunk);
            }
          }
          return { message: text, provider: 'stream' };
        }
        return await response.json();
      } catch (error) {
        console.error('Copilot request failed:', error);
        throw error;
      }
    },
  };
});



